import React, { useState } from 'react'
import SubHeader from '../layout/components/SubHeader'
import TemplateSelectionStep from './TemplateSelectionStep'
import CampaignInformationStep from './CampaignInformationStep'
import SubjectLineStep from './SubjectLineStep'
import EmailContentStep from './EmailContentStep'
import MessageContentStep from './MessageContentStep'
import PatientListStep from './PatientListStep'
import CompleteStep from './CompleteStep'

function CampaignWizard({ 
    title, 
    description, 
    navigatePath, 
    campaignType, // 'email' or 'sms'
    steps = [],
    onSubmit 
}) {
    const [currentStep, setCurrentStep] = useState(1)
    const [emailTemplate, setEmailTemplate] = useState(null)
    const [formData, setFormData] = useState({
        campaignName: '',
        description: '',
        subjectLine: '',
        content: '',
        messageContent: '',
        selectedPatients: []
    })
    const [searchTerm, setSearchTerm] = useState('')
    const [searchFilter, setSearchFilter] = useState('firstName')
    const [showDatePicker, setShowDatePicker] = useState(false)
    const [selectedDate, setSelectedDate] = useState('')
    const [showGenderFilter, setShowGenderFilter] = useState(false)
    const [selectedGender, setSelectedGender] = useState('')
    const [showAgeFilter, setShowAgeFilter] = useState(false)
    const [minAge, setMinAge] = useState('')
    const [maxAge, setMaxAge] = useState('')
    const [templateOption, setTemplateOption] = useState('blank')
    const [templates, setTemplates] = useState([])
    const [selectedTemplate, setSelectedTemplate] = useState(null)
    const [loadingTemplates, setLoadingTemplates] = useState(false)

    const handleNext = () => {
        if (currentStep < steps.length) {
            setCurrentStep(currentStep + 1)
        }
    }

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1)
        } else {
            // Navigate back to list page
            window.history.back()
        }
    }

    const handleSubmit = () => {
        if (onSubmit) {
            onSubmit(formData)
        }
    }

    const renderStepContent = () => {
        const step = steps.find(s => s.id === currentStep)
        if (!step) return null

        switch (step.component) {
            case 'TemplateSelection':
                return (
                    <TemplateSelectionStep 
                        templateOption={templateOption}
                        setTemplateOption={setTemplateOption}
                        templates={templates}
                        setTemplates={setTemplates}
                        selectedTemplate={selectedTemplate}
                        setSelectedTemplate={setSelectedTemplate}
                        loadingTemplates={loadingTemplates}
                        setLoadingTemplates={setLoadingTemplates}
                    />
                )
            case 'CampaignInformation':
                return (
                    <CampaignInformationStep 
                        formData={formData}
                        setFormData={setFormData}
                    />
                )
            case 'SubjectLine':
                return (
                    <SubjectLineStep 
                        formData={formData}
                        setFormData={setFormData}
                    />
                )
            case 'EmailContent':
                return (
                    <EmailContentStep 
                        emailTemplate={emailTemplate}
                        setEmailTemplate={setEmailTemplate}
                        subjectLine={formData.subjectLine}
                    />
                )
            case 'MessageContent':
                return (
                    <MessageContentStep 
                        formData={formData}
                        setFormData={setFormData}
                    />
                )
            case 'PatientList':
                return (
                    <PatientListStep 
                        formData={formData}
                        setFormData={setFormData}
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        searchFilter={searchFilter}
                        setSearchFilter={setSearchFilter}
                        showDatePicker={showDatePicker}
                        setShowDatePicker={setShowDatePicker}
                        selectedDate={selectedDate}
                        setSelectedDate={setSelectedDate}
                        showGenderFilter={showGenderFilter}
                        setShowGenderFilter={setShowGenderFilter}
                        selectedGender={selectedGender}
                        setSelectedGender={setSelectedGender}
                        showAgeFilter={showAgeFilter}
                        setShowAgeFilter={setShowAgeFilter}
                        minAge={minAge}
                        setMinAge={setMinAge}
                        maxAge={maxAge}
                        setMaxAge={setMaxAge}
                        searchOptions={campaignType === 'email' ? ['firstName', 'lastName', 'email'] : ['firstName', 'lastName', 'phone']}
                    />
                )
            case 'Complete':
                const additionalFields = []
                if (campaignType === 'email') {
                    additionalFields.push({ label: 'Subject Line', value: formData.subjectLine })
                } else {
                    additionalFields.push({ label: 'Message Length', value: `${formData.messageContent.length} characters` })
                }
                return (
                    <CompleteStep 
                        formData={formData}
                        additionalFields={additionalFields}
                    />
                )
            default:
                return null
        }
    }

    return (
        <>
            <SubHeader title={title} description={description}>
                <div className="d-flex justify-content-end gap-3">
                    <button 
                        className="btn btn-light-secondary"
                        onClick={handleBack}
                    >
                        {currentStep === 1 ? 'Cancel' : 'Back'}
                    </button>
                    {currentStep < steps.length ? (
                        <button 
                            className="btn btn-primary"
                            onClick={handleNext}
                        >
                            Next
                        </button>
                    ) : (
                        <button 
                            className="btn btn-success"
                            onClick={handleSubmit}
                        >
                            Launch Campaign
                        </button>
                    )}
                </div>
            </SubHeader>
            
            <div className="container-fluid px-6 py-8">
                {/* Step Progress Bar */}
                <div className="mb-10">
                    <div className="d-flex align-items-center position-relative" style={{ minHeight: '50px' }}>
                        {steps.map((step, index) => (
                            <React.Fragment key={step.id}>
                                <div className="d-flex flex-column align-items-center position-relative" style={{ zIndex: 1 }}>
                                    <div 
                                        className={`d-flex align-items-center justify-content-center rounded-circle mb-2 ${
                                            currentStep > step.id 
                                                ? 'bg-success text-white' 
                                                : currentStep === step.id 
                                                    ? 'bg-primary text-white' 
                                                    : 'bg-light text-muted'
                                        }`}
                                        style={{ width: '50px', height: '50px', fontSize: '1.25rem', fontWeight: '600' }}
                                    >
                                        {currentStep > step.id ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                <path d="M8 12L11 15L16 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                                            </svg>
                                        ) : step.id}
                                    </div>
                                    <div className="text-center">
                                        <div className={`fw-bold ${currentStep >= step.id ? 'text-gray-900' : 'text-muted'}`}>
                                            {step.title}
                                        </div>
                                        <div className="text-muted fs-7">{step.subtitle}</div>
                                    </div>
                                </div>
                                {index < steps.length - 1 && (
                                    <div 
                                        className="flex-grow-1"
                                        style={{ 
                                            height: '2px',
                                            backgroundColor: currentStep > step.id ? '#17C653' : '#e4e6ef',
                                            margin: '0 10px',
                                            marginTop: '25px'
                                        }}
                                    ></div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                {/* Main Content Card */}
                <div className="card border-0 shadow-sm">
                    <div className="card-body p-8">
                        {renderStepContent()}
                    </div>
                </div>
            </div>
        </>
    )
}

export default CampaignWizard
